'use client';

import { c as _c } from "react/compiler-runtime";
import { BasicStore, DetailedStore, type DetailedStoreWithDistance, Location } from './interfaces';
import { ReactElement, SetStateAction, memo, useCallback, useEffect, useRef } from 'react';
import { STORE_QUERY_PARAM, getStoreQuerySearch, getStoreSlug } from '@spa/lib/storeSlug';
import { UseUserLocation, useUserLocation } from '@spa/lib/react/hooks/useUserLocation';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import { DynamicLeafletMapComponent } from './leaflet-map/dynamic-leaflet-map.component';
import type { Map as LeafletMap } from 'leaflet';
import type { ProductInfoProduct } from '@spa/components/product-info/ProductInfoProductTypes';
import StoreSearchComponent from './store-search/store-search.component';
import logger from '../../lib/logger';
import styles from './storefinder.component.module.scss';
import useObjectReducer from '@spa/lib/react/hooks/useObjectReducer';
import { useRouter } from 'next/navigation';
import { useStoresAPI } from '@spa/lib/react/hooks/useStoresAPI';
export interface Props {
  readonly id: string;
  readonly selectedStoreCode: string | null;
  readonly favoriteStore?: DetailedStore | null;
  readonly stores: ReadonlyArray<BasicStore>;
  readonly handleDeliverToStore?: (customOrderType: CustomOrderType, quantity: number, selectedStore?: DetailedStore | null) => void;
  readonly isHomepage?: boolean;
  readonly product?: ProductInfoProduct;
  readonly quantityToBuy?: number;
  readonly setSelectedStore?: (selectedStore: DetailedStore | null) => void;
}
async function flyToSearchResults(map: LeafletMap, location: Location, stores: ReadonlyArray<DetailedStoreWithDistance>): Promise<void> {
  const {
    latLngBounds
  } = await import('leaflet');
  const storeLocations = stores.map(store => store.geoLocation);
  storeLocations.push(location);
  const bounds = latLngBounds(storeLocations);
  map.flyToBounds(bounds, {
    maxZoom: 13,
    animate: true,
    duration: 1.5
  });
}
function StorefinderComponent(t0) {
  const $ = _c(57);
  const {
    id,
    stores,
    selectedStoreCode,
    favoriteStore,
    setSelectedStore,
    product,
    isHomepage: t1
  } = t0;
  const isHomepage = t1 === undefined ? false : t1;
  const initialStores = stores;
  const t2 = favoriteStore ?? null;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = favoriteStore ?? null;
  let t5;
  if ($[1] !== initialStores || $[2] !== t2 || $[3] !== t4) {
    t5 = {
      allBasicStores: initialStores,
      centerUserLocation: false,
      isLoadingInitialStores: true,
      newSelectedStore: t2,
      searchResultStores: t3 as ReadonlyArray<DetailedStoreWithDistance>,
      shownBasicStores: initialStores,
      shownStore: t4
    };
    $[1] = initialStores;
    $[2] = t2;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [state, updateState] = useObjectReducer(t5);
  const hasFetchedStoresRef = useRef(false);
  const mapRef = useRef(null);
  const {
    allBasicStores,
    centerUserLocation,
    isLoadingInitialStores,
    newSelectedStore,
    searchResultStores,
    shownBasicStores,
    shownStore
  } = state;
  const {
    location
  } = useUserLocation();
  const {
    getStores,
    setMyStoreId,
    getInitialDetailedStores
  } = useStoresAPI();
  const {
    push
  } = useRouter();
  let t6;
  if ($[5] !== updateState) {
    t6 = value => {
      updateState(prev => ({
        shownBasicStores: typeof value === "function" ? value(prev.shownBasicStores) : value
      }));
    };
    $[5] = updateState;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const setShownBasicStores = t6;
  let t7;
  if ($[7] !== updateState) {
    t7 = value_0 => {
      updateState(prev_0 => ({
        searchResultStores: typeof value_0 === "function" ? value_0(prev_0.searchResultStores) : value_0
      }));
    };
    $[7] = updateState;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const setSearchResultStores = t7;
  let t8;
  if ($[9] !== isHomepage || $[10] !== push || $[11] !== updateState) {
    t8 = value_1 => {
      const store = typeof value_1 !== "function" ? value_1 : value_1(null);
      if (isHomepage && store) {
        const currentSearch = typeof window !== "undefined" ? window.location.search : "";
        const searchTerm = new URLSearchParams(currentSearch).get(STORE_QUERY_PARAM);
        push(`/s/${getStoreSlug(store)}${getStoreQuerySearch(searchTerm)}`);
      } else {
        updateState(prev_1 => ({
          shownStore: typeof value_1 === "function" ? value_1(prev_1.shownStore) : value_1
        }));
      }
    };
    $[9] = isHomepage;
    $[10] = push;
    $[11] = updateState;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const setShownStore = t8;
  let t9;
  if ($[13] !== updateState) {
    t9 = value_2 => {
      updateState(prev_2 => ({
        centerUserLocation: typeof value_2 === "function" ? value_2(prev_2.centerUserLocation) : value_2
      }));
    };
    $[13] = updateState;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const setCenterUserLocation = t9;
  let t10;
  if ($[15] !== updateState) {
    t10 = value_3 => {
      updateState(prev_3 => ({
        isLoadingInitialStores: typeof value_3 === "function" ? value_3(prev_3.isLoadingInitialStores) : value_3
      }));
    };
    $[15] = updateState;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const setIsLoadingInitialStores = t10;
  let t11;
  if ($[17] !== setIsLoadingInitialStores) {
    t11 = () => {
      setIsLoadingInitialStores(false);
    };
    $[17] = setIsLoadingInitialStores;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleHydratedSearchSettled = t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = map => {
      mapRef.current = map;
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const setMapRef = t12;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (location_0, stores_0) => {
      const map_0 = mapRef.current;
      if (typeof window === "undefined" || stores_0.length === 0 || !map_0) {
        return;
      }
      flyToSearchResults(map_0, location_0, stores_0);
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const centerMapToSearchResults = t13;
  let t14;
  if ($[21] !== setMyStoreId || $[22] !== setSelectedStore || $[23] !== updateState) {
    t14 = newSelectedStore_0 => {
      setMyStoreId(newSelectedStore_0.code);
      updateState({
        newSelectedStore: newSelectedStore_0
      });
      if (setSelectedStore) {
        setSelectedStore(newSelectedStore_0);
      }
    };
    $[21] = setMyStoreId;
    $[22] = setSelectedStore;
    $[23] = updateState;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const setSelectedStoreCallback = t14;
  let t15;
  let t16;
  if ($[25] !== getInitialDetailedStores || $[26] !== getStores || $[27] !== isHomepage || $[28] !== setIsLoadingInitialStores || $[29] !== updateState) {
    t15 = () => {
      let cancelled = false;
      if (!hasFetchedStoresRef.current) {
        const hasUrlSearch = isHomepage && typeof window !== "undefined" && (new URLSearchParams(window.location.search).get(STORE_QUERY_PARAM) ?? "").trim().length > 0;
        const fetchStores = async () => {
          ;
          try {
            const newStores = await getStores();
            hasFetchedStoresRef.current = true;
            if (!cancelled) {
              updateState({
                allBasicStores: newStores,
                shownBasicStores: newStores
              });
              if (!hasUrlSearch) {
                const initialDetailedStores = await getInitialDetailedStores(newStores);
                if (!cancelled) {
                  updateState({
                    isLoadingInitialStores: false,
                    searchResultStores: initialDetailedStores
                  });
                }
              }
            }
          } catch (t17) {
            const error = t17;
            if (!cancelled) {
              setIsLoadingInitialStores(false);
              logger.error(error, "Error fetching stores:");
            }
          }
        };
        fetchStores();
      }
      return () => {
        cancelled = true;
        hasFetchedStoresRef.current = false;
      };
    };
    t16 = [getStores, getInitialDetailedStores, setIsLoadingInitialStores, updateState, isHomepage];
    $[25] = getInitialDetailedStores;
    $[26] = getStores;
    $[27] = isHomepage;
    $[28] = setIsLoadingInitialStores;
    $[29] = updateState;
    $[30] = t15;
    $[31] = t16;
  } else {
    t15 = $[30];
    t16 = $[31];
  }
  useEffect(t15, t16);
  const t17 = `${id}-map`;
  let t18;
  if ($[32] !== centerUserLocation || $[33] !== isHomepage || $[34] !== location || $[35] !== setShownStore || $[36] !== shownBasicStores || $[37] !== t17) {
    t18 = <div className={styles["storefinder__map-wrapper"]}><DynamicLeafletMapComponent id={t17} isHomepage={isHomepage} mapRef={setMapRef} shownBasicStores={shownBasicStores} setShownStore={setShownStore} userLocation={location} useUserLocation={centerUserLocation} /></div>;
    $[32] = centerUserLocation;
    $[33] = isHomepage;
    $[34] = location;
    $[35] = setShownStore;
    $[36] = shownBasicStores;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = `${id}-search`;
  const t20 = selectedStoreCode ?? newSelectedStore?.code ?? null;
  let t21;
  if ($[39] !== allBasicStores || $[40] !== handleHydratedSearchSettled || $[41] !== isHomepage || $[42] !== isLoadingInitialStores || $[43] !== product || $[44] !== searchResultStores || $[45] !== setCenterUserLocation || $[46] !== setSearchResultStores || $[47] !== setSelectedStoreCallback || $[48] !== setShownBasicStores || $[49] !== setShownStore || $[50] !== shownStore || $[51] !== t19 || $[52] !== t20) {
    t21 = <StoreSearchComponent allBasicStores={allBasicStores} id={t19} isLoadingInitialStores={isLoadingInitialStores} isHomepage={isHomepage} onHydratedSearchSettled={handleHydratedSearchSettled} searchResultStores={searchResultStores} selectedStoreCode={t20} shownStore={shownStore} centerMapToSearchResults={centerMapToSearchResults} setShownBasicStores={setShownBasicStores} setSelectedStore={setSelectedStoreCallback} setSearchResultStores={setSearchResultStores} setShownStore={setShownStore} setCenterUserLocation={setCenterUserLocation} product={product} />;
    $[39] = allBasicStores;
    $[40] = handleHydratedSearchSettled;
    $[41] = isHomepage;
    $[42] = isLoadingInitialStores;
    $[43] = product;
    $[44] = searchResultStores;
    $[45] = setCenterUserLocation;
    $[46] = setSearchResultStores;
    $[47] = setSelectedStoreCallback;
    $[48] = setShownBasicStores;
    $[49] = setShownStore;
    $[50] = shownStore;
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t18 || $[55] !== t21) {
    t22 = <div className={styles.storefinder}>{t18}{t21}</div>;
    $[54] = t18;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
}
export default memo(StorefinderComponent);